import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
} from '@mui/material';
import { 
  People as PeopleIcon,
  Security as SecurityIcon,
  AllInclusive as AllInclusiveIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

const About = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  // Features with icons
  const features = [
    {
      icon: <PaymentIcon fontSize="large" sx={{ color: theme.palette.primary.light }} />,
      title: 'Transparent and Flexible Pricing',
      description: 'Pay only for what you use, with clear pricing — no hidden fees or forced subscriptions. Read more in our FAQs',
    },
    {
      icon: <AllInclusiveIcon fontSize="large" sx={{ color: theme.palette.primary.light }} />,
      title: 'All-in-One Event Solution',
      description: 'No more switching between event hosting, survey, newsletters, or schedulers. MyEventShop does it all.',
    },
    {
      icon: <SecurityIcon fontSize="large" sx={{ color: theme.palette.primary.light }} />,
      title: 'GDPR-Compliant and Secure',
      description: 'Data protection is at our core, ensuring compliance with international privacy laws, ensured by industry experts.',
    },
    {
      icon: <PeopleIcon fontSize="large" sx={{ color: theme.palette.primary.light }} />,
      title: 'Built for Community',
      description: 'Unlike traditional platforms, we foster engagement, allowing customers to follow creators, leave reviews, and interact.',
    },
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            About Us
          </Typography>
          <Typography 
            variant="h6"
            sx={{ 
              maxWidth: '800px', 
              mx: 'auto', 
              mb: 4,
              color: 'text.secondary'
            }}
          >
            At MyEventShop, we believe in the power of connection, collaboration, and community.
          </Typography>
        </Box>

        {/* Mission Statement */}
        <Paper 
          elevation={0} 
          sx={{
            bgcolor: 'rgba(95, 60, 111, 0.05)',
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
          }}
        >
          <Typography variant="body1" paragraph sx={{ fontSize: '1.05rem' }}>
            Whether you're an individual creator, a small business, or a large organization, our platform
            is designed to simplify event management and marketing while maximizing engagement.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.05rem' }}>
            Our mission is simple: to bring all your event needs under one roof—ticketing, social media
            scheduling, email marketing, analytics, and community engagement—so you can focus on
            creating unforgettable experiences.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.05rem', mb: 0 }}>
            By combining AI-driven workflows with our team's expertise, we are accelerating progress while keeping our long-term vision in focus.
          </Typography>
        </Paper>

        {/* What Makes Us Different Section */}
        <Box sx={{ mb: 8 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            color="primary"
            fontWeight="bold" 
            textAlign="center"
            mb={4}
          >
            What Makes Us Different?
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {feature.icon}
                    <Typography variant="body1" component="h3" fontWeight="bold" ml={1.5}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Conclusion Section */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 8,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="p" mb={3} fontStyle="italic">
            At MyEventShop, we don't just help you host and market events; we empower you to build connections,
            grow your audience, and create lasting impact.
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: 'white',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
              px: 4,
              py: 1,
              fontSize: '1rem',
              fontWeight: 'bold',
              mt: 2,
            }}
            onClick={() => navigate('/')}
          >
            Join the conversation
          </Button>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default About; 